/**
 * <SchoolAssessmentCard> — the header row of an assessment accordion (molecule, L0.1).
 *
 * Domain-aware tier: it takes a `UISchoolAssessment` (from `src/ports/primary/ui/types`) and does its own
 * display formatting (date → "D MMM", `daysUntil` → "Aujourd'hui" / "J-n", urgency → color).
 * It composes DS atoms only — `<ImageIcon>` (subject), `<Title>` / `<Text>` (labels),
 * `<Badge>` (countdown), `<Icon>` (chevron), `<ProgressBar>` (preparation) — and renders the
 * progress bar full-width beneath the row.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { ImageIcon } from "../../atoms/icons/ImageIcon"
import { ProgressBar } from "../../atoms/ProgressBar"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { type SpacingKey } from "../../theme/tokens/spacing"
import { type UISchoolAssessment } from "../../../ports/primary/ui/types"
import { daysUntil, formatDayMonth, formatDaysUntil } from "../../../ports/primary/ui/formatters/dateFormatter"
import { preparationProgress } from "../../../ports/primary/ui/formatters/schoolAssessmentFormatter"

/**
 * Urgency → color token. The literal values (`as const`) are valid both as a
 * `BadgeVariant` and as a `SemanticColorKey`, so the same map drives the countdown
 * badge, the progress fill, and the organism's colored rail.
 */
export const URGENCY_VARIANT = {
    low: "success",
    medium: "warning",
    high: "warning",
    critical: "danger",
} as const

export type SchoolAssessmentCardProps = {
    assessment: UISchoolAssessment
    /** Passed by the Accordion to flip the chevron. */
    expanded?: boolean
    /** Chevron de fin — `true` par défaut. `false` quand la carte n'ouvre rien (en-tête de feuille). */
    chevron?: boolean
    /** Retrait intérieur — `"4"` par défaut. `"0"` quand la surface d'accueil en pose déjà un. */
    p?: SpacingKey
    testID?: string
}

export function SchoolAssessmentCard(props: SchoolAssessmentCardProps) {
    const { assessment, expanded = false, chevron = true, p = "4", testID } = props
    const { title, subject, date, chaptersProgress, urgency } = assessment

    const variant = URGENCY_VARIANT[urgency]
    const dateLabel = formatDayMonth(date)
    const days = daysUntil(date)
    const daysLabel = formatDaysUntil(days)
    const prep = preparationProgress(chaptersProgress)

    return (
        <Stack gap="2" style={styles.container(p)} testID={testID}>
            <Row justify="between" align="center">
                <Row gap="3" align="center" style={styles.shrink}>
                    <ImageIcon uri={subject.iconUrl} size={32} />
                    <Stack gap="1" style={styles.shrink}>
                        <Title size="text-lg" numberOfLines={1}>
                            {title}
                        </Title>
                        <Row gap="2" align="center">
                            <Text size="text-xs" color="text-muted">
                                {dateLabel}
                            </Text>
                            <Badge text={daysLabel} variant={variant} />
                        </Row>
                    </Stack>
                </Row>
                <Row gap="2" align="center">
                    <Text weight="bold">{prep}%</Text>
                    {chevron && <Icon name={expanded ? "chevron-up" : "chevron-down"} color="text-muted" size={16} />}
                </Row>
            </Row>
            <ProgressBar value={prep} color={variant} />
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    container: (p: SpacingKey) => ({
        padding: theme.spacing[p],
    }),
    shrink: {
        flexShrink: 1,
    },
}))
